import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { api } from '@/lib/api';
import { DashboardHomeClient } from '@/components/dashboard-home-client';

export default function DashboardHome(): import('react/jsx-runtime').JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    staleTime: 60 * 1000,
  });

  const projectList = data?.projects ?? [];
  const projectCount = projectList.length;
  const projectProps: { id: string; name: string; agentUrl: string | null }[] = projectList.map(
    (p) => ({
      id: p.id ?? '',
      name: p.name ?? '',
      agentUrl: p.agentUrl ?? null,
    }),
  );

  return (
    <div className="mx-auto max-w-[1600px] w-full px-6 md:px-8 py-8 space-y-8 animate-fade-in">
      <div className="flex items-center justify-between gap-6">
        {/* Left: Title */}
        <div className="space-y-1 shrink-0">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Command Hub</h1>
          <p className="text-sm text-muted-foreground/60 flex items-center gap-1.5">
            Full fidelity replay available for{' '}
            {projectCount === 1 && projectList[0]?.name != null ? (
              <span className="font-mono text-foreground/80 px-1 bg-white/[0.03] rounded">
                {projectList[0].name}
              </span>
            ) : (
              <span className="font-mono text-foreground/80 px-1 bg-white/[0.03] rounded">
                {projectCount} project{projectCount !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>

        {/* Right: CTA + status */}
        <div className="flex items-center gap-3">
          {/* Explorer CTA */}
          <Link
            to="/explorer"
            className="group flex items-center gap-2.5 px-4 py-2.5 rounded-[10px] bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-blue-500/30 text-sm font-medium text-foreground/80 hover:text-foreground transition-all shadow-sm"
          >
            <span className="flex items-center justify-center w-5 h-5 rounded-md bg-blue-500/15 border border-blue-500/20">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-blue-400"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </span>
            <span>Execution Explorer</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-muted-foreground/40 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </Link>

          <div className="h-5 w-px bg-white/10 mx-1" />

          {/* Status badges */}
          <div className="px-3 py-1.5 rounded-[6px] bg-[#1a1b1e] border border-white/5 text-[9px] font-mono tracking-widest text-muted-foreground uppercase">
            REFRESH_RATE: 2s
          </div>
          <div className="px-3 py-1.5 rounded-[6px] bg-[var(--flux-success)]/10 border border-[var(--flux-success)]/20 text-[9px] font-mono tracking-widest text-[var(--flux-success)] shadow-[0_0_15px_var(--flux-success)]/[0.1] uppercase font-bold">
            SYSTEM STEADY — FIDELITY 100%
          </div>
        </div>
      </div>

      <DashboardHomeClient projects={projectProps} />
    </div>
  );
}
